import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CandidateSessionConfiguration } from "src/app/models/models";

export type CandidateSessionConfigurationInput = string | CandidateSessionConfiguration;

function dedupeCandidateSessionConfigurations(items: CandidateSessionConfiguration[]): CandidateSessionConfiguration[] {
	const seen = new Set<string>();
	return items.filter((item) => {
		const key = `${item.code ?? ""}|${item.description}|${item.selectionRemarks ?? ""}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

/** Normalize API summary session data (legacy strings or objects with selectionRemarks). */
export function normalizeCandidateSessionConfigurations(
	raw?: CandidateSessionConfigurationInput[] | null,
): CandidateSessionConfiguration[] {
	if (!raw?.length) return [];
	const first = raw[0];
	if (typeof first === "string") {
		return dedupeCandidateSessionConfigurations(
			(raw as string[])
				.map((description) => ({ description: description.trim() }))
				.filter((item) => item.description.length > 0),
		);
	}
	return dedupeCandidateSessionConfigurations(
		(raw as CandidateSessionConfiguration[])
			.map((item) => ({
				code: item?.code,
				description: (item?.description ?? "").trim(),
				selectionRemarks: item?.selectionRemarks?.trim() || undefined,
			}))
			.filter((item) => item.description.length > 0),
	);
}

export function formatCandidateSessionDescriptions(configs: CandidateSessionConfiguration[]): string {
	return configs
		.map((c) => {
			const description = c.description?.trim() ?? "";
			const remarks = c.selectionRemarks?.trim();
			if (remarks) {
				return `${description} : ${remarks}`;
			}
			return description;
		})
		.filter(Boolean)
		.join(", ");
}

export function sessionDetailCodeVariants(code: string): string[] {
	const normalized = (code ?? "").trim();
	if (!normalized) return [];
	return Array.from(new Set([normalized, normalized.replace(/\//g, "_"), normalized.replace(/_/g, "/")]));
}

function normalizeNestedSessionMap(raw: unknown): Record<string, Record<string, unknown>> {
	const source = Array.isArray(raw) ? raw[0] : raw;
	if (!source || typeof source !== "object") return {};
	return source as Record<string, Record<string, unknown>>;
}

export function isSessionDetailSelected(sectionValue: Record<string, unknown>, code: string): boolean {
	for (const variant of sessionDetailCodeVariants(code)) {
		if (sectionValue[variant] === true) return true;
	}
	return false;
}

export function resolveSessionDetailRemark(remarksSection: Record<string, unknown>, code: string): string {
	for (const variant of sessionDetailCodeVariants(code)) {
		const remark = (remarksSection[variant] ?? "").toString().trim();
		if (remark) return remark;
	}
	return "";
}

export function extractCandidateSessionConfigurations(
	sessionDetailsRaw: unknown,
	sessionRemarksRaw: unknown,
	sessionKeys: any[] = [],
): CandidateSessionConfiguration[] {
	const sessionDetails = normalizeNestedSessionMap(sessionDetailsRaw);
	const sessionRemarks = normalizeNestedSessionMap(sessionRemarksRaw);
	const configs: CandidateSessionConfiguration[] = [];

	for (const section of sessionKeys ?? []) {
		const sectionCode = section?.name?.code ?? section?.code;
		if (!sectionCode) continue;

		const sectionValue = sessionDetails[sectionCode] ?? {};
		const remarksSection = sessionRemarks[sectionCode] ?? {};

		for (const d of section?.details ?? []) {
			const code = typeof d === "string" ? d : d?.code;
			const description = (typeof d === "string" ? d : (d?.description ?? code))?.toString().trim();
			if (!code || !isSessionDetailSelected(sectionValue, code)) continue;

			const remark = resolveSessionDetailRemark(remarksSection, code);
			configs.push({
				description: description || code,
				selectionRemarks: remark || undefined,
			});
		}
	}

	return configs;
}

/** Turn raw sessionDetails (array or object) into { [sectionCode]: Set<detailCode> } */
export function toPreselectedMap(raw: any): Record<string, Set<string>> {
	const src = Array.isArray(raw) ? (raw[0] ?? {}) : (raw ?? {});
	const map: Record<string, Set<string>> = {};

	for (const [sectionKey, value] of Object.entries(src)) {
		const arr = Array.isArray(value) ? (value as string[]) : [];
		map[sectionKey] = new Set(arr.filter((x) => typeof x === "string" && x.length > 0));
	}
	return map;
}

/**
 * Build the sessionDetails FormGroup dynamically from backend `sessionKeys`.
 * Pre-checks based only on `preMap` contents (no hardcoded section names).
 */
export function buildSessionDetailsGroup(
	fb: UntypedFormBuilder,
	sessionKeys: any[] = [],
	preMap: Record<string, Set<string>> = {},
): UntypedFormGroup {
	const root = fb.group({});

	// Helper: find a matching Set in preMap with exact or case-insensitive section key
	const getSelectedSet = (sectionCode: string): Set<string> => {
		if (preMap[sectionCode]) return preMap[sectionCode];
		const foundKey = Object.keys(preMap).find(
			(k) => k === sectionCode || k.toLowerCase() === sectionCode.toLowerCase(),
		);
		return foundKey ? preMap[foundKey] : new Set<string>();
	};

	for (const section of Array.isArray(sessionKeys) ? sessionKeys : []) {
		const sectionCode = (section?.name && section?.name?.code) ?? section?.code ?? section?.key ?? null;

		if (!sectionCode || typeof sectionCode !== "string") continue;

		const details = Array.isArray(section?.details) ? section.details : [];
		const controls: Record<string, UntypedFormControl> = {};
		const selected = getSelectedSet(sectionCode);

		for (const d of details) {
			const code = typeof d === "string" ? d : d?.code;
			if (!code || typeof code !== "string") continue;
			controls[code] = fb.control(selected.has(code), { updateOn: "change" });
		}

		root.addControl(sectionCode, fb.group(controls), { emitEvent: false });
	}

	root.updateValueAndValidity({ emitEvent: false });
	return root;
}

/** Convenience: build and set candidateForm.sessionDetails silently */
export function applySessionDetailsToForm(
	candidateForm: UntypedFormGroup,
	fb: UntypedFormBuilder,
	sessionKeys: any[],
	rawSessionDetails: any,
) {
	const preMap = toPreselectedMap(rawSessionDetails);
	const group = buildSessionDetailsGroup(fb, sessionKeys, preMap);
	candidateForm.setControl("sessionDetails", group, { emitEvent: false });
	candidateForm.updateValueAndValidity({ emitEvent: false });
}
